import { NotificationData, NotificationItem } from "@/api/definitions";

// Notification rendering metadata shared between the notifications page
// (NotificationCard) and the top bar dropdown (NotificationsDropdown),
// so titles, messages, icons and accents cannot drift between the two views.

export interface NotificationContent {
    title: string;
    message: string;
    icon: string;
    accent: string;

    /**
     * True if the notification is caused by an actor (participant or user)
     * and the message expects the actor as the sentence subject.
     */
    hasActor: boolean;

    /**
     * True if the notification carries a reason (terminations and rejections).
     */
    hasReason: boolean;

    /**
     * True if the notification message expects a dataset label.
     */
    hasDataset: boolean;
}

export const RULEBOOK_NOTIFICATION_ROUTES: Record<string, string> = {
    "rulebook:inherited:received": "/admin/data-space?tab=rulebook-versions",
    "rulebook:inherited:rules_changed": "/admin/data-space?tab=rulebook-rules",
    "rulebook:conflicts:proposed": "/admin/data-space?tab=rulebook-conflicts",
};

export function getNotificationDatasetLabel(data: NotificationData, $t: (t: string) => string): string {
    return data.datasetTitle || data.datasetId || $t("Unknown target");
}

export function getNotificationContent(type: string, $t: (t: string) => string): NotificationContent {
    switch (type) {
        case "negotiation:request":
            return {
                title: $t("New negotiation request"),
                message: $t("made a negotiation request for the target"),
                icon: "fa-solid fa-hand-holding-dollar",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:offer":
            return {
                title: $t("New negotiation offer"),
                message: $t("made a negotiation offer for the target"),
                icon: "fas fa-file-signature",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:accept":
            return {
                title: $t("Accepted negotiation offer"),
                message: $t("accepted your negotiation offer for the target"),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:agree":
            return {
                title: $t("Agreement created"),
                message: $t("created an agreement for the target"),
                icon: "fas fa-handshake-simple",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:verify":
            return {
                title: $t("Agreement verified"),
                message: $t("verified an agreement for the target"),
                icon: "fas fa-shield-halved",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:finalize":
            return {
                title: $t("Negotiation finalized"),
                message: $t("finalized a negotiation for the target"),
                icon: "fas fa-flag-checkered",
                accent: "success",
                hasActor: true,
                hasReason: false,
                hasDataset: true,
            };
        case "negotiation:terminate":
            return {
                title: $t("Negotiation terminated"),
                message: $t("terminated a negotiation for the target"),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: true,
                hasReason: true,
                hasDataset: true,
            };
        case "negotiation:rulebook_rejected":
            return {
                title: $t("Negotiation rejected by the rulebook"),
                message: $t("had a request rejected by the rulebook for the dataset"),
                icon: "fas fa-scale-balanced",
                accent: "danger",
                hasActor: true,
                hasReason: true,
                hasDataset: true,
            };
        case "negotiation:rulebook_review":
            return {
                title: $t("Negotiation left for review by the rulebook"),
                message: $t("had a request left for manual review by the rulebook for the dataset"),
                icon: "fas fa-scale-balanced",
                accent: "warning",
                hasActor: true,
                hasReason: true,
                hasDataset: true,
            };
        case "rulebook:inherited:received":
            return {
                title: $t("New rulebook version received"),
                message: $t("published a new version of its rulebook. It does not apply here until it is accepted"),
                icon: "fas fa-scale-balanced",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "rulebook:inherited:rules_changed":
            return {
                title: $t("Rules of an inherited rulebook changed"),
                message: $t("changed the rules it publishes for its rulebook. The new and reworded ones are pending review here"),
                icon: "fas fa-scale-balanced",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "rulebook:conflicts:proposed":
            return {
                title: $t("Rulebook conflicts to decide"),
                message: $t(
                    "The assistant found contradictions between rules of the rulebooks in force. Until they are decided, an own rule prevails over an inherited one, and between two of the same origin, the one approved first.",
                ),
                icon: "fas fa-code-compare",
                accent: "warning",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "agreement:dispute:new_messages":
            return {
                title: $t("New dispute messages for an agreement"),
                message: $t("There are new dispute messages for an agreement."),
                icon: "fas fa-comments",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "agreement:dispute:lock":
            return {
                title: $t("Dispute was locked"),
                message: $t("The clearing house locked the dispute. No new messages are allowed."),
                icon: "fas fa-lock",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "agreement:dispute:unlock":
            return {
                title: $t("Dispute was unlocked"),
                message: $t("The clearing house unlocked the dispute. New messages may be sent by the participants."),
                icon: "fas fa-unlock",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "provider:request:received":
            return {
                title: $t("Received provider registration request"),
                message: $t("made a provider registration request"),
                icon: "fas fa-user-plus",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };

        case "marketplace:subscription:ended:stopped":
            return {
                title: $t("Application stopped"),
                message: $t("The license for the application ended, so it was stopped:"),
                icon: "fas fa-circle-stop",
                accent: "warning",
                hasActor: false,
                hasReason: false,
                hasDataset: true,
            };
        case "marketplace:subscription:ended:stop_failed":
            return {
                title: $t("Application not stopped"),
                message: $t("The license for the application ended, but it could not be stopped. Stop it by hand:"),
                icon: "fas fa-triangle-exclamation",
                accent: "danger",
                hasActor: false,
                hasReason: false,
                hasDataset: true,
            };
        case "marketplace:subscription:ended:running":
            return {
                title: $t("License ended"),
                message: $t("The license for the application ended, and it keeps running:"),
                icon: "fas fa-circle-info",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: true,
            };
        case "marketplace:subscription:ended:not_running":
            return {
                title: $t("License ended"),
                message: $t("The license for the application ended. It was not running:"),
                icon: "fas fa-circle-info",
                accent: "info",
                hasActor: false,
                hasReason: false,
                hasDataset: true,
            };
        case "agreement:subscription:ended":
            return {
                title: $t("Agreement ended"),
                message: $t("The agreement over this asset ended:"),
                icon: "fas fa-circle-stop",
                accent: "warning",
                hasActor: false,
                hasReason: false,
                hasDataset: true,
            };
        case "marketplace:publish_request:received":
            return {
                title: $t("Received app publication request"),
                message: $t("requested to publish an application in the app marketplace"),
                icon: "fas fa-store",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "broker:request:accepted":
            return {
                title: $t("Accepted broker registration request"),
                message: $t("The metadata broker accepted the request."),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "broker:request:rejected":
            return {
                title: $t("Rejected broker registration request"),
                message: $t("The metadata broker rejected the request."),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: false,
                hasReason: true,
                hasDataset: false,
            };
        case "attestation:request:received":
            return {
                title: $t("Received attestation issuance request"),
                message: $t("made an attestation issuance request"),
                icon: "fas fa-stamp",
                accent: "info",
                hasActor: true,
                hasReason: false,
                hasDataset: false,
            };
        case "attestator:response:accepted":
            return {
                title: $t("Accepted attestation issuance request"),
                message: $t("The attestator accepted the request."),
                icon: "fas fa-circle-check",
                accent: "success",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
        case "attestator:response:rejected":
            return {
                title: $t("Rejected attestation issuance request"),
                message: $t("The attestator rejected the request."),
                icon: "fas fa-circle-xmark",
                accent: "danger",
                hasActor: false,
                hasReason: true,
                hasDataset: false,
            };
        default:
            return {
                title: $t("Unknown notification"),
                message: $t("Unknown notification"),
                icon: "fas fa-bell",
                accent: "neutral",
                hasActor: false,
                hasReason: false,
                hasDataset: false,
            };
    }
}

export function getNotificationMessage(notification: NotificationItem, $t: (key: string) => string): string {
    const content = getNotificationContent(notification.type, $t);

    let message = content.message || content.title;

    if (content.hasActor) {
        message = getNotificationAuthor(notification, $t) + " " + message;
    }
    if (content.hasDataset) {
        message += " " + getNotificationDatasetLabel(notification.data, $t);
    }
    if (content.hasReason) {
        message += (message.endsWith(".") ? " " : ". ") + $t("Reason") + ": " + (notification.data.reason || $t("No reason provided"));
    }

    return message;
}

export function getNotificationAuthor(notification: NotificationItem, $t: (key: string) => string): string {
    if (notification.data.remote) {
        return notification.data.participantName || notification.data.participantId || $t("A remote participant");
    }
    if (notification.data.username) return "@" + notification.data.username;
    return $t("A local process");
}

export function getNotificationLink(notification: NotificationItem): string | null {
    const type = notification.type;
    const data = notification.data;
    if (!data) return null;

    if (data.negotiation) {
        return `/negotiation/${data.negotiation}`;
    }
    if (data.agreement) {
        const base = data.isClearingHouse ? `/clearing-house/agreements/${data.agreement}` : `/agreement/${data.agreement}`;
        return type === "agreement:dispute:new_messages" ? `${base}?tab=dispute` : base;
    }
    if (RULEBOOK_NOTIFICATION_ROUTES[type]) {
        return RULEBOOK_NOTIFICATION_ROUTES[type];
    }
    if (data.id) {
        const requestRoutes: Record<string, string> = {
            "provider:request:received": `/catalog-broker-requests/received/${data.id}`,
            "broker:request:accepted": `/catalog-broker-requests/sent/${data.id}`,
            "broker:request:rejected": `/catalog-broker-requests/sent/${data.id}`,
            "attestation:request:received": `/attestation-requests/received/${data.id}`,
            "attestator:response:accepted": `/attestation-requests/sent/${data.id}`,
            "attestator:response:rejected": `/attestation-requests/sent/${data.id}`,
            "marketplace:publish_request:received": `/app-marketplace/moderation/${data.id}`,
        };
        return requestRoutes[type] || null;
    }

    return null;
}
